import React, { useState } from 'react';
import MindMapViewer from './MindMapViewer';

function toAmt(v) {
  const n = Number(String(v || '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function fmt(v) {
  return `RM ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function truncate(str, max = 42) {
  const s = String(str || '').trim();
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

const TABS = [
  { id: 'overview',  label: '📊 Overview'     },
  { id: 'analysis',  label: '🔍 Analysis'     },
  { id: 'transactions', label: '💳 Transactions' },
  { id: 'mindmap',   label: '🗺️ Mind Map'     },
];

export default function AnalysisTabs({ notebookData, portfolioMode }) {
  const [tab, setTab] = useState('overview');

  const {
    summary = '',
    keyInsights = [],
    riskSignals = [],
    nextActions = [],
    crossDocumentFindings = [],
    incomeCategories = [],
    liabilitySignals = [],
    transactionHighlights = [],
    transactionTable = [],
    financialHealthAssessment = '',
    mindMapMermaid = '',
    entities = {},
  } = notebookData || {};

  const rows = Array.isArray(transactionTable) ? transactionTable : [];
  const totalDebit  = rows.reduce((s, r) => s + toAmt(r.debit),  0);
  const totalCredit = rows.reduce((s, r) => s + toAmt(r.credit), 0);
  const netCash     = totalCredit - totalDebit;
  const incomeRows  = rows.filter((r) => /income|sales|receipt/i.test(r.category || '') || toAmt(r.credit) > 0).length;
  const liabRows    = rows.filter((r) => /debt|loan|charge|liability|financing/i.test(r.category || '')).length;

  const maxDebit  = Math.max(...rows.map((r) => toAmt(r.debit)),  1);
  const maxCredit = Math.max(...rows.map((r) => toAmt(r.credit)), 1);

  const topDebits = rows
    .map((r) => ({ label: `${r.date || ''} ${r.description || 'Transaction'}`, amount: toAmt(r.debit), category: r.category || 'Other' }))
    .filter((r) => r.amount > 0)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 10)
    .map((r) => ({ ...r, pct: Math.max(6, Math.round((r.amount / maxDebit) * 100)) }));

  const topCredits = rows
    .map((r) => ({ label: `${r.date || ''} ${r.description || 'Transaction'}`, amount: toAmt(r.credit), category: r.category || 'Other' }))
    .filter((r) => r.amount > 0)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 10)
    .map((r) => ({ ...r, pct: Math.max(6, Math.round((r.amount / maxCredit) * 100)) }));

  return (
    <>
      {/* Tab bar */}
      <div className="nb-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`nb-tab${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div className="nb-tab-content">
          <div className="overview-grid">
            <div className="ov-card">
              <div className="ov-card-label">Period</div>
              <div className="ov-card-value" style={{ fontSize: 13 }}>
                {entities?.periodCovered || 'Not detected'}
              </div>
            </div>
            <div className="ov-card">
              <div className="ov-card-label">Banks</div>
              <div className="ov-card-value" style={{ fontSize: 13 }}>
                {(entities?.banks || []).slice(0, 2).join(', ') || 'Not detected'}
              </div>
            </div>
            <div className="ov-card">
              <div className="ov-card-label">Risk Signals</div>
              <div className="ov-card-value" style={{ color: riskSignals.length ? 'var(--danger)' : 'var(--success)' }}>
                {riskSignals.length}
              </div>
            </div>
            <div className="ov-card">
              <div className="ov-card-label">Scope</div>
              <div className="ov-card-value" style={{ fontSize: 13 }}>
                {portfolioMode ? 'Portfolio' : 'Single doc'}
              </div>
            </div>
          </div>

          {summary && (
            <div className="nb-block">
              <div className="nb-block-title">
                <span className="nb-block-icon icon-blue">📋</span>
                Executive Summary
              </div>
              <p className="summary-text">{summary}</p>
            </div>
          )}

          {portfolioMode && financialHealthAssessment && (
            <div className="nb-block">
              <div className="nb-block-title">
                <span className="nb-block-icon icon-green">❤️</span>
                Financial Health Assessment
              </div>
              <p className="summary-text">{financialHealthAssessment}</p>
            </div>
          )}

          <div className="nb-split">
            <div className="nb-block" style={{ marginBottom: 0 }}>
              <div className="nb-block-title">
                <span className="nb-block-icon icon-purple">💡</span>
                Key Findings
              </div>
              {keyInsights.length
                ? <ul>{keyInsights.slice(0, 5).map((item, i) => <li key={i}>{item}</li>)}</ul>
                : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No findings yet. Run Analyze.</p>}
            </div>

            <div className="nb-block" style={{ marginBottom: 0 }}>
              <div className="nb-block-title">
                <span className="nb-block-icon icon-yellow">⚡</span>
                Next Actions
              </div>
              {nextActions.length
                ? <ul>{nextActions.slice(0, 5).map((item, i) => <li key={i}>{item}</li>)}</ul>
                : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No actions generated yet.</p>}
            </div>
          </div>
        </div>
      )}

      {/* ── ANALYSIS ── */}
      {tab === 'analysis' && (
        <div className="nb-tab-content">
          <div className="nb-block">
            <div className="nb-block-title">
              <span className="nb-block-icon icon-red">⚠️</span>
              Risk Signals
            </div>
            {riskSignals.length
              ? <ul>{riskSignals.map((item, i) => <li key={i}>{item}</li>)}</ul>
              : <p style={{ fontSize: 12, color: 'var(--success)' }}>✓ No risk flags identified.</p>}
          </div>

          <div className="nb-block">
            <div className="nb-block-title">
              <span className="nb-block-icon icon-blue">🔗</span>
              {portfolioMode ? 'Cross-Document Findings' : 'Transaction Themes'}
            </div>
            {crossDocumentFindings.length
              ? <ul>{crossDocumentFindings.map((item, i) => <li key={i}>{item}</li>)}</ul>
              : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No patterns extracted yet.</p>}
          </div>

          {!portfolioMode && (
            <div className="nb-block">
              <div className="nb-block-title">
                <span className="nb-block-icon icon-purple">🏦</span>
                Entities Detected
              </div>
              <div className="tag-list" style={{ marginBottom: 12 }}>
                {(entities?.banks || []).map((b, i) => <span key={i} className="tag">🏦 {b}</span>)}
                {(entities?.companyNames || []).map((c, i) => <span key={i} className="tag">🏢 {c}</span>)}
                {(entities?.accounts || []).map((a, i) => <span key={i} className="tag">🔢 {a}</span>)}
                {!entities?.banks?.length && !entities?.companyNames?.length && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No entities detected.</span>
                )}
              </div>
              {entities?.periodCovered && (
                <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                  📅 Period: <strong>{entities.periodCovered}</strong>
                </div>
              )}
            </div>
          )}

          {portfolioMode && (
            <div className="nb-split">
              <div className="nb-block" style={{ marginBottom: 0 }}>
                <div className="nb-block-title">
                  <span className="nb-block-icon icon-green">💰</span>
                  Income Categories
                </div>
                {incomeCategories.length
                  ? <ul>{incomeCategories.map((item, i) => <li key={i}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>)}</ul>
                  : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not segmented yet.</p>}
              </div>

              <div className="nb-block" style={{ marginBottom: 0 }}>
                <div className="nb-block-title">
                  <span className="nb-block-icon icon-red">📉</span>
                  Liability Signals
                </div>
                {liabilitySignals.length
                  ? <ul>{liabilitySignals.map((item, i) => <li key={i}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>)}</ul>
                  : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>None identified yet.</p>}
              </div>
            </div>
          )}

          {portfolioMode && transactionHighlights.length > 0 && (
            <div className="nb-block">
              <div className="nb-block-title">
                <span className="nb-block-icon icon-yellow">🌟</span>
                Notable Transactions
              </div>
              <ul>{transactionHighlights.map((item, i) => (
                <li key={i}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>
              ))}</ul>
            </div>
          )}
        </div>
      )}

      {/* ── TRANSACTIONS ── */}
      {tab === 'transactions' && (
        <div className="nb-tab-content">
          {portfolioMode && (
            <div className="txn-metrics">
              <div className="txn-metric">
                <div className="txn-metric-label">Total Credits</div>
                <div className="txn-metric-value positive">{fmt(totalCredit)}</div>
              </div>
              <div className="txn-metric">
                <div className="txn-metric-label">Total Debits</div>
                <div className="txn-metric-value negative">{fmt(totalDebit)}</div>
              </div>
              <div className="txn-metric">
                <div className="txn-metric-label">Net Cash</div>
                <div className={`txn-metric-value ${netCash >= 0 ? 'positive' : 'negative'}`}>
                  {fmt(netCash)}
                </div>
              </div>
              <div className="txn-metric">
                <div className="txn-metric-label">Income / Liability Rows</div>
                <div className="txn-metric-value">{incomeRows} / {liabRows}</div>
              </div>
            </div>
          )}

          <div className="txn-table-wrap">
            <table className="txn-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Debit (RM)</th>
                  <th>Credit (RM)</th>
                  {portfolioMode && <th>Source</th>}
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((row, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap' }}>{row.date || 'N/A'}</td>
                    <td>{row.description || 'N/A'}</td>
                    <td><span className="txn-chip">{row.category || 'Other'}</span></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmt(toAmt(row.debit))}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmt(toAmt(row.credit))}</td>
                    {portfolioMode && <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.sourceDocument || 'Portfolio'}</td>}
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={portfolioMode ? 6 : 5} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                      {portfolioMode
                        ? 'Run portfolio analysis to populate the transaction table.'
                        : 'No transaction data available for single documents. Run portfolio analysis.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {portfolioMode && (topDebits.length > 0 || topCredits.length > 0) && (
            <div className="bar-charts">
              <div className="bar-chart-card">
                <div className="bar-chart-title">🔴 Top 10 Largest Debits</div>
                {topDebits.length === 0
                  ? <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>No debit rows.</p>
                  : topDebits.map((item, i) => (
                    <div key={i} className="bar-row">
                      <div className="bar-row-head">
                        <span className="bar-row-label">{truncate(item.label)}</span>
                        <span className="bar-row-value">{fmt(item.amount)}</span>
                      </div>
                      <div className="bar-track">
                        <div className="bar-fill debit" style={{ width: `${item.pct}%` }} />
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{item.category}</div>
                    </div>
                  ))}
              </div>

              <div className="bar-chart-card">
                <div className="bar-chart-title">🟢 Top 10 Largest Credits</div>
                {topCredits.length === 0
                  ? <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>No credit rows.</p>
                  : topCredits.map((item, i) => (
                    <div key={i} className="bar-row">
                      <div className="bar-row-head">
                        <span className="bar-row-label">{truncate(item.label)}</span>
                        <span className="bar-row-value">{fmt(item.amount)}</span>
                      </div>
                      <div className="bar-track">
                        <div className="bar-fill credit" style={{ width: `${item.pct}%` }} />
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{item.category}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MIND MAP ── */}
      {tab === 'mindmap' && (
        <div className="nb-tab-content">
          <div className="mindmap-wrap">
            {portfolioMode && mindMapMermaid?.trim()
              ? <MindMapViewer chart={mindMapMermaid} />
              : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, gap: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
                  <div style={{ fontSize: 40, opacity: 0.3 }}>🗺️</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Mind map not available</div>
                  <div style={{ fontSize: 13 }}>
                    {portfolioMode
                      ? 'Mind map data was not returned. Run portfolio analysis again.'
                      : 'Select multiple sources and run Portfolio Analysis to generate a mind map.'}
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </>
  );
}
